import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/shared/components/ui/dialog";
import { Button } from "@/shared/components/ui/button";
import { Input } from "@/shared/components/ui/input";
import { Textarea } from "@/shared/components/ui/textarea";
import { resolveMutationErrorMessage } from "@/shared/api/error-message";
import { useCreateProduct } from "./use-create-product";
import { PRODUCT_DESCRIPTION_MAX_LENGTH, PRODUCT_NAME_MAX_LENGTH } from "./constants";
import { ProductMetadataFields } from "./ProductMetadataFields";
import {
  buildCreateMetadata,
  isRankInputValid,
  type ProductMetadataValue,
} from "./product-metadata";

const EMPTY_METADATA: ProductMetadataValue = {
  tier: "",
  access: [],
  rank: "",
};

interface CreateProductDialogProps {
  readonly open: boolean;
  readonly onOpenChange: (open: boolean) => void;
  readonly onSuccess: () => void;
}

export function CreateProductDialog({ open, onOpenChange, onSuccess }: CreateProductDialogProps) {
  const { t } = useTranslation();
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [metadata, setMetadata] = useState<ProductMetadataValue>(EMPTY_METADATA);
  const [error, setError] = useState<string | null>(null);
  const create = useCreateProduct();

  useEffect(() => {
    if (open) {
      setName("");
      setDescription("");
      setMetadata(EMPTY_METADATA);
      setError(null);
    }
  }, [open]);

  const trimmedName = name.trim();
  const trimmedDescription = description.trim();
  const isValid =
    trimmedName.length > 0 &&
    trimmedName.length <= PRODUCT_NAME_MAX_LENGTH &&
    trimmedDescription.length <= PRODUCT_DESCRIPTION_MAX_LENGTH &&
    isRankInputValid(metadata.rank);

  const handleClose = (next: boolean) => {
    if (!next && create.isPending) return;
    onOpenChange(next);
  };

  const handleSubmit = () => {
    if (!isValid) return;
    setError(null);
    const metadataBag = buildCreateMetadata(metadata);
    create.mutate(
      {
        name: trimmedName,
        ...(trimmedDescription.length > 0 ? { description: trimmedDescription } : {}),
        ...(metadataBag ? { metadata: metadataBag } : {}),
      },
      {
        onSuccess: () => onSuccess(),
        onError: (err) => {
          setError(
            resolveMutationErrorMessage(err, t, {
              forbiddenKey: "catalog.errors.forbidden",
              serverPrefixKey: "catalog.errors.serverPrefix",
              genericKey: "catalog.products.create.error",
            }),
          );
        },
      },
    );
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent data-testid="create-product-dialog">
        <DialogHeader>
          <DialogTitle>{t("catalog.products.create.title")}</DialogTitle>
          <DialogDescription>{t("catalog.products.create.description")}</DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          <div className="space-y-1.5">
            <label
              htmlFor="create-product-name"
              className="font-sans text-sm font-medium text-foreground"
            >
              {t("catalog.products.fields.name")}
            </label>
            <Input
              id="create-product-name"
              data-testid="create-product-name-input"
              value={name}
              onChange={(e) => setName(e.target.value)}
              maxLength={PRODUCT_NAME_MAX_LENGTH}
              disabled={create.isPending}
            />
          </div>

          <div className="space-y-1.5">
            <label
              htmlFor="create-product-description"
              className="font-sans text-sm font-medium text-foreground"
            >
              {t("catalog.products.fields.description")}
            </label>
            <Textarea
              id="create-product-description"
              data-testid="create-product-description-input"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              maxLength={PRODUCT_DESCRIPTION_MAX_LENGTH}
              rows={3}
              disabled={create.isPending}
            />
          </div>

          <ProductMetadataFields
            value={metadata}
            onChange={setMetadata}
            disabled={create.isPending}
            idPrefix="create-product"
          />

          {error && (
            <p
              data-testid="create-product-error"
              role="alert"
              className="font-sans text-sm font-medium text-destructive"
            >
              {error}
            </p>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => handleClose(false)} disabled={create.isPending}>
            {t("catalog.actions.cancel")}
          </Button>
          <Button
            data-testid="confirm-create-product-button"
            onClick={handleSubmit}
            disabled={!isValid || create.isPending}
          >
            {create.isPending
              ? t("catalog.products.create.pending")
              : t("catalog.products.actions.create")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
